import { useMemo } from 'react';
import { DATE_FORMAT, RELEVANCES, TimePeriods } from 'constants/date';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isBetween from 'dayjs/plugin/isBetween';
import dayjs_plugin_utc from 'dayjs/plugin/utc';
import { ISelectOption } from 'interfaces';
import { LocalStorageWithDates } from 'interfaces/localStorage';
import { TDateRange } from 'types';

dayjs.extend(dayjs_plugin_utc);
dayjs.extend(customParseFormat);
dayjs.extend(isBetween);

export const getFormattedDate = (
  date: Date | string | null | undefined,
  format = 'DD.MM.YY HH:mm',
  keepUTCLocalTime = true
) => {
  if (date) {
    return dayjs(date).utc(keepUTCLocalTime).format(format);
  }

  return null;
};

export const getFormattedDateRange = (
  dateRange: TDateRange,
  format: string = DATE_FORMAT,
  formatter: (startDate: string, endDate: string) => string = (
    startDate,
    endDate
  ) => (startDate === endDate ? startDate : `с ${startDate} по ${endDate}`)
) => {
  const startDate = dayjs(dateRange[0]).format(format);
  const endDate = dayjs(dateRange[1]).format(format);

  return formatter(startDate, endDate);
};

export const getTagDate = (date: string) => {
  const newDates = date.split('-').map((d) => {
    return dayjs(d, 'DD.MM.YY').format(DATE_FORMAT);
  });

  if (newDates.length >= 1) {
    return newDates[0] === newDates[1]
      ? newDates[0]
      : `с ${newDates[0]} по ${newDates[1]}`;
  }

  return '';
};

export const formatDate = (date: Date) => dayjs(date).format(DATE_FORMAT);

export const getDateRangeFromRelevance = (
  relevance: string,
  hasDefaultValue?: boolean
): TDateRange | undefined => {
  const todayStart = dayjs().startOf('day').utc(true).toDate();
  const todayEnd = dayjs().endOf('day').utc(true).toDate();

  const getSubstractedTimePeriod = (number: number, period: TimePeriods) =>
    dayjs().subtract(number, period).startOf('day').utc(true).toDate();

  switch (relevance) {
    case RELEVANCES.ONE_HOUR: {
      todayStart.setHours(todayStart.getHours() - 1);

      return [todayStart, todayEnd];
    }
    case RELEVANCES.TWO_HOURS: {
      todayStart.setHours(todayStart.getHours() - 2);

      return [todayStart, todayEnd];
    }
    case RELEVANCES.FOUR_HOURS: {
      todayStart.setHours(todayStart.getHours() - 4);

      return [todayStart, todayEnd];
    }
    case RELEVANCES.EIGHT_HOURS: {
      todayStart.setHours(todayStart.getHours() - 8);

      return [todayStart, todayEnd];
    }
    case RELEVANCES.TWELVE_HOURS: {
      todayStart.setHours(todayStart.getHours() - 12);

      return [todayStart, todayEnd];
    }
    case RELEVANCES.ONE_DAY: {
      return [todayStart, todayEnd];
    }
    case RELEVANCES.THREE_DAYS: {
      return [getSubstractedTimePeriod(2, TimePeriods.DAY), todayEnd];
    }
    case RELEVANCES.ONE_WEEK: {
      return [getSubstractedTimePeriod(6, TimePeriods.DAY), todayEnd];
    }
    case RELEVANCES.ONE_MONTH: {
      return [getSubstractedTimePeriod(29, TimePeriods.DAY), todayEnd];
    }
    case RELEVANCES.THREE_MONTHS: {
      return [getSubstractedTimePeriod(89, TimePeriods.DAY), todayEnd];
    }
    case RELEVANCES.SIX_MONTHS: {
      return [getSubstractedTimePeriod(6, TimePeriods.MONTH), todayEnd];
    }
    case RELEVANCES.ONE_YEAR: {
      return [getSubstractedTimePeriod(1, TimePeriods.YEAR), todayEnd];
    }
    case RELEVANCES.ALL_TIME: {
      return [new Date(2022, 1, 24), todayEnd];
    }
    default: {
      if (hasDefaultValue) return;
      return [todayStart, todayEnd];
    }
  }
};

export const useLocalStorageDates = <T extends LocalStorageWithDates>(
  localStorageSetting: T,
  fallbackValues?: TDateRange
): TDateRange =>
  useMemo(() => {
    // localStorage returns string instead of date, so we need to wrap it first
    const startDate = localStorageSetting.startDate ?? fallbackValues?.[0];
    const endDate = localStorageSetting.endDate ?? fallbackValues?.[1];
    return [
      startDate ? new Date(startDate) : null,
      endDate ? new Date(endDate) : null,
    ];
  }, [localStorageSetting]);

export const getDisplayRelevanceOrDate = (
  relevance: string,
  options: ISelectOption[],
  startDate: Date | null,
  endDate: Date | null,
  dateFormat = 'DD.MM.YYYY'
) => {
  if (relevance === 'other') {
    const formattedStartDate = dayjs(startDate).format(dateFormat);
    const formattedEndDate = endDate
      ? dayjs(endDate).format(dateFormat)
      : 'НЕ ВЫБРАНО';

    return `${formattedStartDate}-${formattedEndDate}`;
  }

  return options.find((val) => val.value === relevance)?.label ?? 'Неизвестно';
};
